<template>
  <div>
    <b-skeleton-wrapper :loading="isArticleLoadInProgress">
      <template #loading>
        <div class="position-relative skeleton-image-wrapper">
          <b-skeleton-img width="100%" height="565px" animation="fade" />
          <div class="skeleton-image-text-container w-100">
            <b-skeleton animation="fade" class="w-75" />
            <b-skeleton animation="fade" class="w-50" />
          </div>
        </div>
        <div class="container d-xl-flex d-block justify-content-center w-100">
          <div class="skeleton-question-and-rating">
            <b-skeleton animation="fade" class="w-50 question-and-rating-text" />
            <b-skeleton animation="fade" class="w-50 question-and-rating-text" />
            <b-skeleton animation="fade" class="w-75 question-and-rating-date" />
            <hr class="my-4">
            <b-skeleton animation="fade" class="w-75 question-and-rating-stars" />
            <div class="question-and-rating-btn-wrapper">
              <b-skeleton animation="fade" class="w-100 question-and-rating-btn" />
            </div>
          </div>
          <div class="w-100">
            <div class="my-3">
              <div class="d-flex justify-content-center">
                <b-skeleton animation="fade" class="w-75 skeleton-content-large-text" />
              </div>
              <div class="d-flex justify-content-center">
                <b-skeleton animation="fade" class="w-50 skeleton-content-large-text" />
              </div>
            </div>
            <div v-for="index in 10" :key="index" class="mt-4 mb-4">
              <b-skeleton animation="fade" class="w-50" />
              <b-skeleton animation="fade" class="w-100" />
              <b-skeleton animation="fade" class="w-75" />
              <b-skeleton animation="fade" class="w-25" />
            </div>
          </div>
        </div>
      </template>
      <template v-if="article && !isArticleLoadInProgress">
        <div
          itemscope
          itemtype="https://schema.org/Article"
        >
          <div v-if="article && article.imageSrc && article.showImage">
            <div class="article-image-container overlay">
              <img
                :src="article.imageSrc"
                :alt="article.title"
                class="w-100 h-100"
              >
              <h1
                class="container article-h1-title"
                itemprop="name"
                v-text="article.title"
              />
            </div>
          </div>
          <meta
            itemprop="articleSection"
            :content="article.metaData.articleSection"
          >
          <meta
            itemprop="headline"
            :content="article.metaData.headline"
          >
          <div itemprop="publisher" itemscope itemtype="https://schema.org/Organization">
            <meta itemprop="name" :content="article.metaData.publisherName">
            <div itemprop="logo" itemscope itemtype="https://schema.org/ImageObject">
              <meta itemprop="url" :content="article.metaData.publisherLogo">
              <meta itemprop="width" content="171">
              <meta itemprop="height" content="80">
            </div>
          </div>
          <div itemprop="image" itemscope itemtype="https://schema.org/ImageObject">
            <meta itemprop="url" :content="article.metaData.ogImage">
            <meta itemprop="width" content="600">
            <meta itemprop="height" content="314">
          </div>
          <meta
            itemprop="mainEntityOfPage"
            :content="article.metaData.mainEntityOfPage"
          >
          <meta itemprop="datePublished" :content="article.metaData.datePublished">
          <meta itemprop="dateModified" :content="article.metaData.dateModified">
          <div class="w-100 d-flex justify-content-center">
            <div class="article-text container" :style="article.showImage ? '' : 'margin-top: 20px !important;'">
              <h1
                v-if="!article.imageSrc || article.imageSrc === ''"
                class="container article-h1-title text-center"
                itemprop="name"
                v-text="article.title"
              />
              <div class="article-action-container">
                <div
                  v-if="article.author"
                >
                  <span
                    class="author"
                    v-text="article.author"
                  />
                  <meta itemprop="author" :content="article.author">
                  <span
                    v-if="article.authorTitle"
                    class="author-job-title"
                    v-text="article.authorTitle"
                  />
                </div>
                <meta
                  v-else
                  itemprop="author"
                  content="unknown"
                >
                <span
                  v-if="article.hasPublicationDate"
                  v-text="article.publicationDate"
                />
                <hr v-if="article.hasPublicationDate || article.author">
                <div class="d-flex d-xl-block">
                  <favorite-button
                    v-if="isLoggedIn"
                    :item-id="parseInt(article.id)"
                    item-type="article"
                    :is-background-visible="false"
                    class="fav-btn"
                  />
                  <rating-stars
                    class="mt-2"
                    :rating="article.rating"
                    :is-rating-enabled="isLoggedIn && !article.userHasRate"
                    :is-review-count-visible="false"
                    @rateClicked="rateArticle"
                  />
                </div>
                <button
                  v-if="article.isCommentEnabled"
                  v-t="'kerdezek_tittle'"
                  class="custom-button mt-3"
                  data-cy="write-question"
                  @click="$refs['comment-block'].scrollIntoView({ behavior: 'smooth' })"
                />
              </div>
              <div
                v-if="article.lead"
                v-html="article.lead"
              />
              <div v-observe-visibility="textAndWidgetDisplayVisibilityChanged">
                <text-and-widget-display
                  v-if="isTextAndWidgetDisplayVisible"
                  :text-body="article.body"
                  class="article-body"
                />
              </div>
              <div class="d-flex flex-wrap mt-2">
                <article-card
                  v-for="articleData in article.childArticleList"
                  :key="articleData.id"
                  :article="articleData"
                  :is-overlay-type="true"
                  class="col-12 col-md-6 my-2"
                />
                <template v-if="article.hasMoreChildArticle">
                  <loading-indicator v-if="isMoreChildArticlesLoading" />
                  <div v-else class="w-100 d-flex align-items-center justify-content-center my-5">
                    <span
                      v-t="'others'"
                      class="cursor-pointer load-more-btn"
                      @click="loadMoreChildArticles"
                    />
                  </div>
                </template>
              </div>
              <hr>
              <div class="mb-4">
                <div class="parent-title" v-text="article.parentArticleName" />
                <div class="article-title" v-text="article.title" />
              </div>
              <div
                v-if="article.author"
                class="mb-4 d-flex justify-content-between author-box"
              >
                <div class="d-flex">
                  <b-avatar :text="getAuthorInitial()" />
                  <div class="ml-4 d-flex justify-content-center flex-column">
                    <div
                      class="author"
                      v-text="article.author"
                    />
                    <div
                      v-if="article.authorTitle"
                      class="author-job-title"
                      v-text="article.authorTitle"
                    />
                  </div>
                </div>
                <!-- TODO: Ide elvileg jönni az author profiljának és emailjének gombja, de nem létezik ilyen, igy most ez üres egyelőre -->
                <!-- <div class="d-flex align-items-center"> -->
                <!-- </div> -->
              </div>
              <div v-if="article.showRecommendations" class="my-4">
                <div v-if="article.similarArticleList.length > 0">
                  <div
                    v-t="'kapcsolodo_cikkek_label'"
                    class="article-bar-title my-4 col-12 text-center"
                  />
                  <div class="d-flex flex-wrap">
                    <article-card
                      v-for="currentArticle in article.similarArticleList"
                      :key="currentArticle.id"
                      :article="currentArticle"
                      :is-overlay-type="true"
                      class="col-12 col-md-6 my-2"
                    />
                  </div>
                </div>
                <div v-else>
                  <div v-t="'top_articles'" class="text-center my-4" />
                  <article-bar
                    type="TOPLIST"
                    :limit="8"
                    :skip-article-id="article.id"
                  />
                </div>
              </div>
              <div v-if="article.isCommentEnabled" ref="comment-block">
                <div v-if="unAnsweredCommentList.length > 0" class="mb-4">
                  <div v-t="'kerdesed_valaszra_var_article'" class="text-center font-weight-bold" />
                  <comment-card
                    v-for="comment in unAnsweredCommentList"
                    :key="comment.id"
                    :comment="comment"
                    class="my-3"
                    data-cy="comment-card"
                  />
                  <div
                    v-t="'legy_turelemmel_kerdes_article'"
                    class="text-center font-weight-bold"
                    data-cy="be-patient-text"
                  />
                </div>
                <div v-if="unAnsweredCommentList.length === 0" class="my-4">
                  <template v-if="article.canSendQuestion">
                    <custom-input
                      v-if="!isLoggedIn"
                      v-model="email"
                      :title-name="$t('helpdesk_emailcim')"
                      :error-text="errorMsg"
                      :has-error="errorMsg"
                      data-cy="article-question-email-input"
                    />
                    <textarea
                      v-model="questionText"
                      :placeholder="$t('term_kerdesek_irdmegaboxban_article')"
                      data-cy="question-textarea"
                    />
                    <div class="text-center">
                      <button
                        v-t="'term_kerdesek_elkuld'"
                        class="custom-button my-3"
                        :disabled="questionText.length < 15 || isSendingQuestion"
                        data-cy="send-question-btn"
                        @click="sendQuestion"
                      />
                    </div>
                  </template>
                  <div
                    v-else
                    v-t="'nincs_lehetoseg_tobb_kerdesre'"
                    class="text-center"
                    data-cy="no-option-for-recive-answer-text"
                  />
                </div>
                <comment-card
                  v-for="comment in answeredCommentList"
                  :key="comment.id"
                  :comment="comment"
                  class="my-3"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-skeleton-wrapper>
  </div>
</template>

<script>
import Axios from 'axios'
import { NOTY_TYPE } from '../../constant/builderConsts.js'
import { mapGetters } from 'vuex'
import { BAvatar, BSkeleton, BSkeletonImg, BSkeletonWrapper } from 'bootstrap-vue'
import Vue from 'vue'
import { ObserveVisibility } from 'vue-observe-visibility'

Vue.directive('observe-visibility', ObserveVisibility)

export default {
  name: 'ArticlePage',
  components: {
    'loading-indicator': () => import(/* webpackChunkName: "LoadingIndicator" */ '../../components/LoadingIndicator/LoadingIndicator.vue'),
    'rating-stars': () => import(/* webpackChunkName: "RatingStars" */ '../../components/RatingStars/RatingStars.vue'),
    'comment-card': () => import(/* webpackChunkName: "commentCard" */ '../../components/commentCard/commentCard.vue'),
    'article-card': () => import(/* webpackChunkName: "ArticleCard" */ '../../components/ArticleCard/ArticleCard.vue'),
    'article-bar': () => import(/* webpackChunkName: "articleBar" */ '../../components/articleBar/articleBar.vue'),
    'favorite-button': () => import(/* webpackChunkName: "favoriteButton" */ '../../components/favoriteButton/favoriteButton.vue'),
    'text-and-widget-display': () => import(/* webpackChunkName: "textAndWidgetDisplay" */ '../../components/textAndWidgetDisplay/textAndWidgetDisplay.vue'),
    'custom-input': () => import(/* webpackChunkName: "customInput" */ '../../components/customInput/customInput.vue'),
    'b-avatar': BAvatar,
    'b-skeleton-wrapper': BSkeletonWrapper,
    'b-skeleton-img': BSkeletonImg,
    'b-skeleton': BSkeleton,
  },
  data() {
    return {
      /**
       * cikk object
       *
       * @type {{
       *   id: Number,
       *   imageSrc: String,
       *   title: String,
       *   lead: String,
       *   body: String,
       *   author: String,
       *   authorTitle: String,
       *   publicationDate: String,
       *   hasPublicationDate: Boolean,
       *   parentArticleName: String,
       *   rating: Number,
       *   userHasRate: Boolean,
       *   showRecommendations: Boolean,
       *   isCommentEnabled: Boolean,
       *   canSendQuestion: Boolean,
       *   hasMoreChildArticle: Boolean,
       *   showImage: Boolean,
       *   similarArticleList: [{
       *     id: Number,
       *     title: String,
       *     parentTitle: String|null,
       *     imageSrc: String,
       *     url: String,
       *   }],
       *   childArticleList: [{
       *     id: Number,
       *     title: String,
       *     parentTitle: String|null,
       *     imageSrc: String,
       *     url: String,
       *   }],
       *   commentList: [{
       *     id: Number,
       *     answerDate: String|null,
       *     answerText: String|null,
       *     isAnswered: Boolean,
       *     questionDate: String,
       *     questionText: String,
       *   }],
       *   metaData: {
       *     title: String,
       *     articleSection: String,
       *     headline: String,
       *     ogImage: String,
       *     mainEntityOfPage: String,
       *     publisherName: String,
       *     publisherLogo: String,
       *     datePublished: String,
       *     dateModified: String,
       *     url: String,
       *   },
       * }|null}
       */
      article: null,
      /**
       * cikk szöveg és widget megjelenítő látható-e
       *
       * @type {Boolean}
       */
      isTextAndWidgetDisplayVisible: false,
      /**
       * folyamatban van-e a cikk értékelése?
       *
       * @type {Boolean}
       */
      isRatingInProgress: false,
      /**
       * Email a kérdés feltételéhez
       */
      email: '',
      /**
       * cikkhez írandó kérdés szövege
       *
       * @type {String}
       */
      questionText: '',
      /**
       * kérdés beküldése folyamatban van-e
       *
       * @type {Boolean}
       */
      isSendingQuestion: false,
      /**
       * töltődnek-e jelenleg további child cikkek
       *
       * @type {Boolean}
       */
      isMoreChildArticlesLoading: false,
      /**
       * article load folyamatban van-e
       */
      isArticleLoadInProgress: false,
      /**
       * Hiba üzenet
       */
      errorMsg: null,
      /**
       * itemek betöltésére használt api AbortController, ha kell, ha nem akkor null
       *
       * @type {AbortController|null}
       */
      abortController: null,
    }
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'User/isLoggedIn',
    }),
    /**
     * nem megválaszolt kérdések listája
     */
    unAnsweredCommentList() {
      return this.article.commentList.filter(comment => !comment.isAnswered)
    },
    /**
     * megválaszolt kérdések listája
     */
    answeredCommentList() {
      return this.article.commentList.filter(comment => comment.isAnswered)
    },
  },
  created() {
    this.loadArticle(this.$route.meta.isNoRule ? this.$route.query.id : this.$route.params.id, this.$route.meta.isNoRule)
  },
  methods: {
    /**
     * cikk szöveg és widgetek megvan-e jelenitve
     *
     * @param isVisible
     */
    textAndWidgetDisplayVisibilityChanged(isVisible) {
      if (this.isTextAndWidgetDisplayVisible || !isVisible) {
        return
      }
      this.isTextAndWidgetDisplayVisible = true
    },
    /**
     * további child cikkek betöltése
     */
    loadMoreChildArticles() {
      if (this.isMoreChildArticlesLoading) {
        return
      }
      this.isMoreChildArticlesLoading = true
      Axios.get('/Article/api/listMoreChildArticles', {
        params: {
          articleId: this.article.id,
          offset: this.article.childArticleList.length,
        },
      })
        .then(({ data }) => {
          this.$set(this.article, 'childArticleList', [...this.article.childArticleList, ...data.childArticleList])
          this.$set(this.article, 'hasMoreChildArticle', data.hasMoreChildArticle)
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          this.isMoreChildArticlesLoading = false
        })
    },
    /**
     * kérdés beküldése
     */
    sendQuestion() {
      if (this.questionText === '' || this.isSendingQuestion) {
        this.$root.$emit('notificationPush', this.$t('term_kerdeserror_ures'), NOTY_TYPE.NOTY_ERROR)
        return
      }
      if (!this.isLoggedIn && this.email === '') {
        this.$root.$emit('notificationPush', this.$t('email_ures_hiba'), NOTY_TYPE.NOTY_ERROR)
        return
      }
      this.isSendingQuestion = true
      const articleId = this.$route.meta.isNoRule ? this.$route.query.id : this.$route.params.id
      Axios.post('Article/api/sendQuestion', {
        email: this.email,
        questionText: this.questionText,
        articleId: articleId,
      })
        .then(({ data }) => {
          if (data.errorMsg) {
            this.errorMsg = data.errorMsg
            this.$root.$emit('notificationPush', this.errorMsg, NOTY_TYPE.NOTY_ERROR)
            return
          }
          this.errorMsg = null
          this.email = ''
          this.questionText = ''
          this.loadArticle(articleId, this.$route.meta.isNoRule, true)
          this.$root.$emit('notificationPush', this.$t('koszonjuk_kerdeset'), NOTY_TYPE.NOTY_SUCCESS)
        })
        .catch((error) => {
          this.$root.$emit('notificationPush', this.$t('sikertelen_muvelet'), NOTY_TYPE.NOTY_ERROR)
          console.error(error)
        })
        .finally(() => {
          this.isSendingQuestion = false
        })
    },
    /**
     * cikk értékelése
     *
     * @param rateValue
     */
    rateArticle(rateValue) {
      if (this.isRatingInProgress) {
        return
      }
      this.isRatingInProgress = true
      const articleId = this.$route.meta.isNoRule ? this.$route.query.id : this.$route.params.id
      Axios.post('Article/api/rate', {
        rateValue: rateValue,
        id: articleId,
      })
        .then(() => {
          this.loadArticle(articleId, this.$route.meta.isNoRule, true)
          this.isRatingInProgress = false
          this.$root.$emit('notificationPush', this.$t('cikk_ertekeles_sikeres'), NOTY_TYPE.NOTY_SUCCESS)
        })
        .catch((e) => {
          this.isRatingInProgress = false
          console.error('rateArticle', e)
          this.$root.$emit('notificationPush', this.$t('cikk_ertekeles_sikertelen'), NOTY_TYPE.NOTY_ERROR)
        })
    },
    /**
     * cikk szerző monogramja
     *
     * @return {string}
     */
    getAuthorInitial() {
      const authorSplit = this.article.author.split(' ')
      return authorSplit.reduce((accumulator, authorNamePart) => accumulator + authorNamePart[0], '')
    },
    /**
     * cikk betöltése
     *
     * @param id
     * @param isNoRule
     * @param isForceRefresh
     */
    loadArticle(id, isNoRule, isForceRefresh = false) {
      if (!isForceRefresh && this.article !== null && this.article.id === parseInt(id)) {
        return
      }
      if (this.abortController !== null) {
        this.abortController.abort()
      }
      this.abortController = new AbortController()
      this.isArticleLoadInProgress = true
      return Axios.post('Article/api/getArticle', {
        articleId: id,
        isNoRule: isNoRule,
        routeHistory: window.routeHistory, // debug célból, hogy honnan jönnek a nem public, langpublic és nem rootban cikk hivások
      }, {
        signal: this.abortController.signal,
      })
        .then(({ data }) => {
          this.article = data
          const articleId = this.$route.meta.isNoRule ? this.$route.query.id : this.$route.params.id
          if (articleId === id && !isNoRule && this.$route.path !== this.article.url) {
            this.$router.replace({
              path: this.article.url,
              query: {
                ...this.$route.query,
              },
            })
          }
          this.changeMetaTagContent('title', this.article.metaData.title)

          // 5 mp után Klaviyo-ban Viewed Article esemény meghívása
          if (typeof klaviyo !== 'undefined') {
            setTimeout(function () {
              const item = {
                "Title": data.metaData.title,
                "ArticleID": data.id,
                "Author": data.authorTitle,
                "PublicationDate": data.publicationDate,
                "ImageURL": window.location.protocol + "//" + window.location.host + data.imageSrc,
                "URL": window.location.protocol + "//" + window.location.host + data.url
              }
              klaviyo.track("Viewed Article", item)
            }, 5 * 1000)
          }
        })
        .catch((e) => {
          this.article = null
          console.error('loadArticle', e)
        })
        .finally(() => {
          this.isArticleLoadInProgress = false
          this.abortController = null
        })
    }
  },
}
</script>

<style scoped lang="scss">
.article-text {
  position: relative;
  margin-top: 100px;
  min-height: 300px;

  @media screen and (min-width: 768px) {
    max-width: 736px;
  }

  .article-action-container {
    top: 0;
    left: -200px;
    width: 160px;
    margin-bottom: 2rem;

    @media screen and (min-width: 1200px) {
      position: absolute;
      margin-bottom: 0;
    }

    @media screen and (max-width: 400px) {
      width: 100%;

      .custom-button {
        width: 100%;
      }
    }

    .fav-btn {
      margin-left: -10px;
      margin-top: -5px;
      position: relative;
    }

    .author-job-title {
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
      color: #4F4F4F;
    }

    .author {
      font-size: 14px;
      line-height: 16px;
      font-weight: 500;
      color: #111111;
    }
  }

  .parent-title {
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
  }

  .article-title {
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
  }

  .author-box {
    .b-avatar {
      width: 4.5rem;
      height: 4.5rem;
    }

    .author-job-title {
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
      color: #4F4F4F;
    }

    .author {
      font-size: 16px;
      line-height: 24px;
      font-weight: 500;
      color: #111111;
    }
  }
}

.article-image-container {
  width: 100%;
  height: 565px;
  position: relative;
  display: flex;
  justify-content: center;

  &.overlay::after {
    content: "";
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
    height: 100%;
    left: 0;
    overflow: hidden;
    position: absolute;
    bottom: 0;
    width: 100%;
  }

  h1.article-h1-title {
    position: absolute;
    bottom: 0;
    color: #fff;
    z-index: 1;
    margin-top: 0;
    margin-bottom: 4rem;
    font-weight: 800;
    font-size: 40px;
    line-height: 48px;

    @media screen and (min-width: 768px) {
      max-width: 736px;
    }
  }

  img {
    object-fit: cover;
  }
}

h1.article-h1-title {
  margin-bottom: 4rem;
  font-weight: 800;
  font-size: 40px;
  line-height: 48px;

  @media screen and (min-width: 576px) {
    padding: 0;
  }

  @media screen and (min-width: 768px) {
    max-width: 736px;
  }
}

textarea {
  width: 100%;
  resize: none;
  height: 150px;
  padding: 15px;
  border-radius: 5px;
  border-color: #adadad;
}

.skeleton-image-wrapper {
  height: 565px;
  margin-bottom: 100px;

  .skeleton-image-text-container {
    position: absolute;
    bottom: 20px;
    left: 20px;
  }
}

.skeleton-question-and-rating {
  width: 160px;
  margin-right: 45px;

  .question-and-rating-text {
    height: 24px;
  }

  .question-and-rating-date {
    height: 20px;
  }

  .question-and-rating-stars {
    height: 16px;
  }

  .question-and-rating-btn-wrapper {
    margin-top: 16px;

    .question-and-rating-btn {
      height: 45px;
    }
  }
}

.skeleton-content-large-text {
  height: 48px;
}
</style>
